import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../api/endpoints';
import { storage } from '../api/client';
import { useAuth } from './AuthContext';
import { requestLocationPermission, getCurrentPosition } from '../services/location';

const AppContext = createContext(null);

const LOCATION_KEY = 'kw_location';
const VERTICAL_KEY = 'kw_vertical';

const DEFAULT_LOCATION = {
  city: 'Delhi', latitude: 28.7041, longitude: 77.1325, label: 'Delhi',
};

/**
 * Portal-wide preferences: the delivery/service location and the currently
 * selected service vertical (the "multi service" switcher on the home page).
 */
export const AppProvider = ({ children }) => {
  const [location, setLocationState] = useState(DEFAULT_LOCATION);
  const [vertical, setVerticalState] = useState('all');
  const [config, setConfig] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const savedLoc = storage.get(LOCATION_KEY);
    const savedVertical = storage.get(VERTICAL_KEY);
    if (savedLoc) {
      try { setLocationState(JSON.parse(savedLoc)); } catch (e) { /* corrupt value */ }
    }
    if (savedVertical) setVerticalState(savedVertical);
    api.app.config().then((res) => setConfig(res.data)).catch(() => {});

    /*
     * Ask again now and then. Somebody with the page open on New Year's Eve
     * should see the decoration appear when admin turns it on, rather than
     * the next time they happen to reload.
     */
    const again = setInterval(() => {
      api.app.config().then((res) => setConfig(res.data)).catch(() => {});
    }, 5 * 60 * 1000);
    const onFocus = () => api.app.config().then((res) => setConfig(res.data)).catch(() => {});
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(again); window.removeEventListener('focus', onFocus); };
  }, []);

  const setLocation = useCallback(async (next) => {
    setLocationState(next);
    storage.set(LOCATION_KEY, JSON.stringify(next));
    try {
      await api.user.setLocation({
        latitude: next.latitude, longitude: next.longitude, city: next.city,
      });
    } catch (e) { /* not signed in yet */ }
  }, []);

  /*
   * Where the customer actually is, not "Delhi". Detected once per signed-in
   * session; a city picked by hand (`source: 'manual'`) is never overwritten.
   */
  const { user, mode } = useAuth();
  const detectedFor = useRef(null);
  const [detecting, setDetecting] = useState(false);

  const detectLocation = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      try {
        const saved = storage.get(LOCATION_KEY);
        if (saved && JSON.parse(saved).source === 'manual') return null;
      } catch (e) { /* treat as not set */ }
    }
    setDetecting(true);
    try {
      const ok = await requestLocationPermission();
      if (!ok) return null;
      const fix = await getCurrentPosition();
      if (!fix || !fix.coordinates) return null;
      const [longitude, latitude] = fix.coordinates;
      let city = 'Current location';
      let district = '';
      let state = '';
      let address = '';
      let pincode = '';
      try {
        const a = (await api.maps.reverse({ lat: latitude, lng: longitude })).data || {};
        city = a.city || city;
        district = a.district || '';
        state = a.state || '';
        address = a.address || '';
        pincode = a.pincode || '';
      } catch (e) { /* keep the coordinates */ }
      const next = {
        city, district, state, latitude, longitude, source: 'gps', address, pincode,
        label: [city, state].filter(Boolean).join(', ') || 'Current location',
      };
      await setLocation(next);
      return next;
    } finally { setDetecting(false); }
  }, [setLocation]);

  useEffect(() => {
    if (!user || mode !== 'user') return;
    if (detectedFor.current === user._id) return;
    detectedFor.current = user._id;
    detectLocation();
  }, [user, mode, detectLocation]);

  const setVertical = useCallback((key) => {
    setVerticalState(key);
    storage.set(VERTICAL_KEY, key);
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await api.notifications.unread();
      setUnread(res.data.count || 0);
    } catch (e) { setUnread(0); }
  }, []);

  const value = useMemo(() => ({
    location, setLocation, vertical, setVertical, config, unread, refreshUnread, detectLocation, detecting,
  }), [location, setLocation, vertical, setVertical, config, unread, refreshUnread, detectLocation, detecting]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
export default AppContext;
